import { useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import MDButton from "components/MDButton";
import { slugify } from "components/content/contentUtils";
import { createTag, updateTag } from "api/endpoints/tags";

/**
 * <TagForm> — create / edit a website content tag. Used by the Tags
 * management screen inside a SidePanel.
 */
function TagForm({ websiteId, tag, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(tag?.content_tag_id);

  const [form, setForm] = useState({ name: tag?.name || "", slug: tag?.slug || "" });
  // Once the slug is hand-edited, stop deriving it from the name.
  const [slugTouched, setSlugTouched] = useState(Boolean(tag?.slug));
  const [saving, setSaving] = useState(false);

  const handleName = (event) => {
    const name = event.target.value;
    setForm((prev) => ({ ...prev, name, slug: slugTouched ? prev.slug : slugify(name) }));
  };

  const handleSlug = (event) => {
    setSlugTouched(true);
    setForm((prev) => ({ ...prev, slug: slugify(event.target.value) }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      enqueueSnackbar("Name is required", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), slug: form.slug || slugify(form.name) };
      const result = isEdit
        ? await updateTag(websiteId, tag.content_tag_id, payload)
        : await createTag(websiteId, payload);
      enqueueSnackbar(isEdit ? "Tag updated" : "Tag created", { variant: "success" });
      onSaved(result);
    } catch {
      enqueueSnackbar("Failed to save tag", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack component="form" spacing={3} onSubmit={handleSubmit} noValidate>
      <TextField label="Name" value={form.name} onChange={handleName} fullWidth required />
      <TextField
        label="Slug"
        value={form.slug}
        onChange={handleSlug}
        fullWidth
        helperText="Used in content URLs and filters"
      />
      <MDButton type="submit" color="info" variant="gradient" disabled={saving} fullWidth>
        {isEdit ? "Update Tag" : "Create Tag"}
      </MDButton>
    </Stack>
  );
}

TagForm.defaultProps = {
  websiteId: undefined,
  tag: null,
};

TagForm.propTypes = {
  websiteId: PropTypes.string,
  tag: PropTypes.shape({
    content_tag_id: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  onSaved: PropTypes.func.isRequired,
};

export default TagForm;
